/**
 * Servicio de Gestión de Categorías - V2
 * 
 * Este servicio maneja:
 * - Creación y gestión de categorías personalizadas
 * - Interfaz de usuario para gestionar categorías
 * - Exportación de categorías a CSV
 * - Configuración de colores y cómputos
 * - Población de dropdowns y checkboxes
 */

import React from 'react';
import { useUIStore } from '../../stores';
import { useCategoriesStore, CatastralCategory } from '../../stores/categoriesStore';
import { CATEGORIES } from '../../config/categories';

// Usar la interfaz del store
export type Category = CatastralCategory;

export interface CategoryConfig {
  color: string;
  computo: number;
  description?: string;
}

export class CategoriesManagerService {
  constructor() {
    // Inicializar categorías en el store
    useCategoriesStore.getState().initializeCategories();
  }



  /**
   * Obtiene todas las categorías
   */
  getCategories(): Category[] {
    const store = useCategoriesStore.getState();
    // Forzar inicialización siempre
    store.initializeCategories();
    const categories = store.getAllCategories();
    console.log('🔍 CategoriesManagerService: Categorías obtenidas:', categories);
    return categories;
  }

  /**
   * Obtiene una categoría por ID
   */
  getCategoryById(id: string): Category | null {
    return useCategoriesStore.getState().getCategoryById(id);
  }

  /**
   * Obtiene el color de una categoría
   */
  getCategoryColor(categoryId: string): string {
    const category = this.getCategoryById(categoryId);
    return category?.color || '#cccccc';
  }

  /**
   * Obtiene el cómputo de una categoría
   */
  getCategoryComputo(categoryId: string): number | null {
    const category = this.getCategoryById(categoryId);
    return category?.computo || null;
  }

  /**
   * Obtiene el ID a mostrar para una categoría (fallback por si falta en el store)
   */
  private getDisplayId(category: any): string {
    if (category?.id) return category.id;
    const byLabel = CATEGORIES.find(c => c.label === category?.label);
    if (byLabel) return byLabel.id;
    return '';
  }

  /**
   * Verifica si una categoría es de zonas comunes
   */
  isZonasComunesEdificio(categoryId: string): boolean {
    return categoryId === 'ZC';
  }

  /**
   * Crea una nueva categoría personalizada
   */
  createCategory(categoryData: Omit<Category, 'fechaCreacion' | 'fechaModificacion' | 'isCustom'>): Category {
    const store = useCategoriesStore.getState();
    store.createCustomCategory(categoryData);
    return store.getCategoryById(categoryData.id)!;
  }

  /**
   * Actualiza una categoría existente
   */
  updateCategory(id: string, updates: Partial<Omit<Category, 'id' | 'fechaCreacion' | 'isCustom'>>): Category | null {
    const store = useCategoriesStore.getState();
    store.updateCategory(id, updates);
    return store.getCategoryById(id);
  }

  /**
   * Elimina una categoría personalizada
   */
  deleteCategory(id: string): boolean {
    return useCategoriesStore.getState().deleteCustomCategory(id);
  }

  /**
   * Exporta las categorías a CSV
   */
  exportToCSV(): string {
    return useCategoriesStore.getState().exportToCSV();
  }

  /**
   * Actualiza el cómputo de una categoría en tiempo real
   */
  private updateCategoryComputo(id: string, computo: number | null): void {
    const store = useCategoriesStore.getState();
    const category = store.getCategoryById(id);
    if (category) {
      store.updateCategory(id, { computo });
    }
  }

  /**
   * Guarda los cambios de una categoría
   */
  private saveCategoryChanges(id: string): void {
    const { addNotification } = useUIStore.getState();
    const store = useCategoriesStore.getState();
    const category = store.getCategoryById(id);
    
    if (!category) {
      addNotification({
        type: 'error',
        title: 'Error',
        message: 'Categoría no encontrada'
      });
      return;
    }

    try {
      store.updateCategory(id, {
        computo: category.computo
      });
      
      addNotification({
        type: 'success',
        title: 'Cambios guardados',
        message: 'Los cambios se han guardado exitosamente'
      });
    } catch (error) {
      addNotification({
        type: 'error',
        title: 'Error',
        message: 'No se pudieron guardar los cambios'
      });
    }
  }

  /**
   * Muestra el modal de importación con selector de archivo
   */
  private showImportModal(): void {
    const { addNotification } = useUIStore.getState();

    // Crear un input de archivo invisible
    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.accept = '.csv';
    fileInput.style.display = 'none';
    
    fileInput.onchange = (e: any) => {
      const file = e.target.files[0];
      if (!file) return;

      const reader = new FileReader();
      reader.onload = (e: any) => {
        try {
          const csvContent = e.target.result;
          const result = this.importFromCSV(csvContent);
          
          addNotification({
            type: 'success',
            title: 'Importación completada',
            message: `${result.success} categorías importadas exitosamente`
          });
          
          if (result.errors.length > 0) {
            addNotification({
              type: 'warning',
              title: 'Errores en importación',
              message: `${result.errors.length} errores encontrados: ${result.errors.join(', ')}`
            });
          }
        } catch (error) {
          addNotification({
            type: 'error',
            title: 'Error al importar',
            message: 'No se pudo procesar el archivo CSV'
          });
        }
      };
      
      reader.onerror = () => {
        addNotification({
          type: 'error',
          title: 'Error al leer archivo',
          message: 'No se pudo leer el archivo seleccionado'
        });
      };
      
      reader.readAsText(file);
    };

    // Agregar al DOM temporalmente y hacer clic
    document.body.appendChild(fileInput);
    fileInput.click();
    document.body.removeChild(fileInput);
  }



  /**
   * Importa categorías desde CSV
   */
  private importFromCSV(csvContent: string): { success: number; errors: string[] } {
    return useCategoriesStore.getState().importFromCSV(csvContent);
  }

  /**
   * Muestra el gestor de categorías
   */
  showCategoriesManager(): void {
    const { openModal } = useUIStore.getState();
    const { initializeCategories } = useCategoriesStore.getState();
    
    // Inicializar categorías solo si es necesario
    initializeCategories();
    
    openModal({
      id: 'categories-manager',
      title: 'Gestión de Categorías Catastrales',
      content: this.renderCategoriesManagerContent(),
      size: 'large'
    });
  }

  /**
   * Renderiza el contenido del gestor de categorías
   */
  private renderCategoriesManagerContent(): React.ReactNode {
    // Usar un componente funcional para poder usar hooks
    const CategoriesTable = () => {
      const categories = useCategoriesStore(state => state.categories);
      
      // Inicializar solo una vez al montar el componente
      React.useEffect(() => {
        useCategoriesStore.getState().initializeCategories();
      }, []);
      
      return (
      <div className="categories-manager-content">
        <div className="categories-manager-header">
          <h4>Gestionar Categorías Catastrales</h4>
          <p>Aquí puedes crear, editar y eliminar categorías personalizadas.</p>
        </div>

        <div className="categories-manager-actions">
          <button 
            className="btn btn-primary"
            onClick={() => this.showCreateCategoryModal()}
          >
            + Nueva Categoría
          </button>
          <button 
            className="btn btn-secondary"
            onClick={() => this.showImportModal()}
          >
            Importar CSV
          </button>
          <button 
            className="btn btn-success"
            onClick={() => this.exportCategories()}
          >
            Exportar CSV
          </button>
          
        </div>

                 {/* Información de formato CSV */}
         <div className="import-help">
           <h6>Formato para importación CSV:</h6>
           <p>El archivo CSV debe tener las columnas: <strong>ID</strong>, <strong>Etiqueta</strong>, <strong>Color</strong>, <strong>Cómputo</strong></p>
           <ul>
             <li><strong>ID:</strong> Identificador único de la categoría</li>
             <li><strong>Etiqueta:</strong> Nombre descriptivo</li>
             <li><strong>Color:</strong> Código hex (#rrggbb) o formato RGB</li>
             <li><strong>Cómputo:</strong> Valor numérico entre 0-100 (opcional)</li>
           </ul>
           <p><em>Las categorías que no coincidan con las predefinidas se añadirán como categorías personalizadas. Las categorías predefinidas nunca se eliminan.</em></p>
         </div>

        <div className="categories-list">
          <h5>Categorías Disponibles ({categories.length})</h5>
          <div className="categories-table">
            <table>
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Categoría</th>
                  <th>Cómputo (%)</th>
                  <th>Color</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {categories.map((category, index) => {
                  const displayId = this.getDisplayId(category);
                  return (
                  <tr key={`${displayId || 'category'}-${index}`}>
                    <td style={{ fontFamily: 'monospace', fontWeight: 'bold' }}>{displayId || '-'}</td>
                    <td>{category.label}</td>
                    <td>
                      <input
                        type="number"
                        min="0"
                        max="100"
                        value={category.computo || ''}
                        onChange={(e) => this.updateCategoryComputo(category.id, parseFloat(e.target.value) || null)}
                        style={{ width: '60px', textAlign: 'center' }}
                      />
                    </td>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div 
                          style={{ 
                            width: '20px', 
                            height: '20px', 
                            backgroundColor: category.color,
                            border: '1px solid #ccc',
                            borderRadius: '3px'
                          }}
                        />
                        <span style={{ fontFamily: 'monospace', fontSize: '12px' }}>
                          {category.color}
                        </span>
                      </div>
                    </td>
                    <td>
                      <button 
                        className="btn btn-sm btn-outline-success"
                        onClick={() => this.saveCategoryChanges(category.id)}
                      >
                        Guardar
                      </button>
                      {category.isCustom && (
                        <button 
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => this.deleteCategoryWithConfirm(category.id)}
                          style={{ marginLeft: '4px' }}
                        >
                          Eliminar
                        </button>
                      )}
                    </td>
                  </tr>
                );})}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      );
    };
    
    return <CategoriesTable />;
  }

  /**
   * Muestra el modal para crear una nueva categoría
   */
  private showCreateCategoryModal(): void {
    const { openModal, addNotification } = useUIStore.getState();

    openModal({
      id: 'create-category',
      title: 'Crear Nueva Categoría Catastral',
      content: this.renderCreateCategoryForm(),
      confirmText: 'Crear',
      cancelText: 'Cancelar',
      onConfirm: (formData: any) => {
        try {
                     // Validar que el ID sea único
           const existingCategory = this.getCategoryById(formData.id);
           if (existingCategory) {
                         addNotification({
               type: 'error',
               title: 'Error',
               message: 'Ya existe una categoría con ese ID'
             });
            return;
          }

          this.createCategory(formData);
          addNotification({
            type: 'success',
            title: 'Categoría creada',
            message: 'La categoría se ha creado exitosamente'
          });
        } catch (error) {
          addNotification({
            type: 'error',
            title: 'Error',
            message: 'No se pudo crear la categoría'
          });
        }
      }
    });
  }

  /**
   * Renderiza el formulario para crear una categoría
   */
  private renderCreateCategoryForm(): React.ReactNode {
    return (
      <div className="create-category-form">
                 <div className="form-group">
           <label htmlFor="id">ID de la Categoría</label>
           <input 
             type="text" 
             id="id" 
             name="id" 
             required 
             placeholder="Ej: OFI"
           />
           <small>Identificador único de 3 letras mayúsculas</small>
         </div>
        
        <div className="form-group">
          <label htmlFor="label">Etiqueta de la Categoría</label>
          <input 
            type="text" 
            id="label" 
            name="label" 
            required 
            placeholder="Ej: Oficina"
          />
          <small>Nombre descriptivo que se mostrará</small>
        </div>
        
        <div className="form-group">
          <label htmlFor="color">Color</label>
          <input 
            type="color" 
            id="color" 
            name="color" 
            required 
            defaultValue="#ff6b35"
          />
        </div>
        
        <div className="form-group">
          <label htmlFor="computo">Cómputo (%)</label>
          <input 
            type="number" 
            id="computo" 
            name="computo" 
            min="0" 
            max="100" 
            step="1"
            placeholder="100"
          />
          <small>Porcentaje de cómputo (0-100)</small>
        </div>
      </div>
    );
  }

  /**
   * Muestra el modal para editar una categoría
   */
  private showEditCategoryModal(category: Category): void {
    const { openModal, addNotification } = useUIStore.getState();

    openModal({
      id: 'edit-category',
      title: 'Editar Categoría Catastral',
      content: this.renderEditCategoryForm(category),
      confirmText: 'Guardar',
      cancelText: 'Cancelar',
      onConfirm: (formData: any) => {
        try {
                     // Para categorías personalizadas, validar que el ID sea único
           if (category.isCustom && formData.id !== category.id) {
             const existingCategory = this.getCategoryById(formData.id);
             if (existingCategory) {
               addNotification({
                 type: 'error',
                 title: 'Error',
                 message: 'Ya existe una categoría con ese ID'
               });
               return;
             }
           }

          this.updateCategory(category.id, formData);
          addNotification({
            type: 'success',
            title: 'Categoría actualizada',
            message: 'La categoría se ha actualizado exitosamente'
          });
        } catch (error) {
          addNotification({
            type: 'error',
            title: 'Error',
            message: 'No se pudo actualizar la categoría'
          });
        }
      }
    });
  }

  /**
   * Renderiza el formulario para editar una categoría
   */
  private renderEditCategoryForm(category: Category): React.ReactNode {
    return (
      <div className="edit-category-form">
                 <div className="form-group">
           <label htmlFor="id">ID de la Categoría</label>
           <input 
             type="text" 
             id="id" 
             name="id" 
             defaultValue={category.id}
             required 
             disabled={!category.isCustom}
           />
           <small>Identificador único de 3 letras mayúsculas (no editable para categorías por defecto)</small>
         </div>
        
        <div className="form-group">
          <label htmlFor="label">Etiqueta de la Categoría</label>
          <input 
            type="text" 
            id="label" 
            name="label" 
            defaultValue={category.label}
            required 
          />
          <small>Nombre descriptivo que se mostrará</small>
        </div>
        
        <div className="form-group">
          <label htmlFor="color">Color</label>
          <input 
            type="color" 
            id="color" 
            name="color" 
            defaultValue={category.color}
            required 
          />
        </div>
        
        <div className="form-group">
          <label htmlFor="computo">Cómputo (%)</label>
          <input 
            type="number" 
            id="computo" 
            name="computo" 
            defaultValue={category.computo || ''}
            min="0" 
            max="100" 
            step="1"
          />
          <small>Porcentaje de cómputo (0-100)</small>
        </div>
      </div>
    );
  }

  /**
   * Exporta las categorías a un archivo CSV
   */
  private exportCategories(): void {
    const { addNotification } = useUIStore.getState();
    
    try {
      const csvContent = this.exportToCSV();
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      
      link.setAttribute('href', url);
      link.setAttribute('download', `categorias_catastrales_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      addNotification({
        type: 'success',
        title: 'Exportación completada',
        message: 'Archivo CSV descargado exitosamente'
      });
    } catch (error) {
      addNotification({
        type: 'error',
        title: 'Error',
        message: 'No se pudo exportar el archivo CSV'
      });
    }
  }

  /**
   * Elimina una categoría con confirmación
   */
  private deleteCategoryWithConfirm(id: string): void {
    const { openModal, addNotification } = useUIStore.getState();
    const category = this.getCategoryById(id);
    
    if (!category) return;

    openModal({
      id: 'delete-category',
      title: 'Confirmar eliminación',
      content: (
        <div>
          <p>¿Estás seguro de que quieres eliminar la categoría "{category.label}"?</p>
          <p>Esta acción no se puede deshacer.</p>
        </div>
      ),
      confirmText: 'Eliminar',
      cancelText: 'Cancelar',
      confirmButtonClass: 'btn-danger',
      onConfirm: () => {
        try {
          this.deleteCategory(id);
          addNotification({
            type: 'success',
            title: 'Categoría eliminada',
            message: 'La categoría se ha eliminado exitosamente'
          });
        } catch (error) {
          addNotification({
            type: 'error',
            title: 'Error',
            message: 'No se pudo eliminar la categoría'
          });
        }
      }
    });
  }
}

// Crear instancia global
export const categoriesManager = new CategoriesManagerService();

// Hacer disponible globalmente para compatibilidad
if (typeof window !== 'undefined') {
  (window as any).categoriesManager = categoriesManager;
}
